import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { DateTime } from 'luxon';
import {
  Subject,
  catchError,
  debounceTime,
  of,
  switchMap,
  takeUntil,
  tap
} from 'rxjs';
import {
  AvailabilityOverviewKpis,
  CollectorAvailabilityFilter,
  CollectorAvailabilityOverviewResponse,
  CollectorAvailabilitySkillConfig,
  CollectorAvailabilitySkillOption,
  CollectorAvailabilitySort,
  TeamAvailabilityOverview
} from '../../domain/collector-availability-overview.model';
import { CollectorAvailabilityOverviewService } from '../../services/collector-availability-overview.service';
import {
  AVAILABILITY_DEFAULT_PAGE_SIZE,
  AVAILABILITY_DAY_MINUTES,
  AVAILABILITY_PAGE_SIZE_OPTIONS,
  AVAILABILITY_SLIDER_STEP_MINUTES,
  formatMinuteOfDay
} from './availability-overview.utils';

type AvailabilityTab = 'collectors' | 'teams';

@Component({
  selector: 'app-availability-overview-panel',
  templateUrl: './availability-overview-panel.component.html',
  styleUrls: ['./availability-overview-panel.component.css']
})
export class AvailabilityOverviewPanelComponent implements OnInit, OnDestroy {
  @Input() active = true;
  @Input() fullscreen = false;
  @Output() close = new EventEmitter<void>();
  @Output() fullscreenChange = new EventEmitter<boolean>();

  readonly pageSizeOptions = AVAILABILITY_PAGE_SIZE_OPTIONS;
  readonly dayMinutes = AVAILABILITY_DAY_MINUTES;
  readonly sliderStep = AVAILABILITY_SLIDER_STEP_MINUTES;
  readonly formatMinuteOfDay = formatMinuteOfDay;
  selectedDate = DateTime.now().setZone('Europe/Zurich').toISODate() ?? '';
  team = '';
  query = '';
  availabilityFilter: CollectorAvailabilityFilter = 'availableAtLeastOnce';
  skill = '';
  skillOptions: CollectorAvailabilitySkillOption[] = [];
  sort: CollectorAvailabilitySort = 'name';
  fromMinute = 0;
  toMinute = AVAILABILITY_DAY_MINUTES;
  page = 0;
  pageSize = AVAILABILITY_DEFAULT_PAGE_SIZE;
  activeTab: AvailabilityTab = 'collectors';
  response: CollectorAvailabilityOverviewResponse | null = null;
  loading = false;
  error = '';
  hasPinnedDetails = false;
  clearPinnedTick = 0;

  private readonly request$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();

  constructor(private overviewService: CollectorAvailabilityOverviewService) {}

  ngOnInit(): void {
    this.overviewService
      .getSkillConfig()
      .pipe(
        catchError(() => of(null)),
        takeUntil(this.destroy$)
      )
      .subscribe((config) => {
        this.skillOptions = this.buildSkillOptions(config);
      });

    this.request$
      .pipe(
        debounceTime(400),
        tap(() => {
          this.loading = true;
          this.error = '';
        }),
        switchMap(() =>
          this.overviewService
            .getOverview({
              date: this.selectedDate,
              fromMinute: this.fromMinute,
              toMinute: this.toMinute,
              team: this.team,
              query: this.query,
              skill: this.skill,
              availabilityFilter: this.availabilityFilter,
              sort: this.sort,
              page: this.page,
              pageSize: this.pageSize
            })
            .pipe(
              catchError((error) => {
                this.error = error?.error?.message ?? error?.message ?? 'Could not load availability.';
                return of(null);
              })
            )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((response) => {
        this.response = response;
        this.loading = false;
      });
    this.request$.next();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.request$.complete();
  }

  get kpis(): AvailabilityOverviewKpis | null {
    return this.response?.kpis ?? null;
  }

  get teams(): TeamAvailabilityOverview[] {
    return this.response?.teams ?? [];
  }

  get teamOptions(): string[] {
    return (this.kpis?.teams ?? []).map((item) => item.team);
  }

  get dateCaption(): string {
    const date = DateTime.fromISO(this.selectedDate, { zone: 'Europe/Zurich' });
    return date.isValid ? `${date.toFormat('ccc d LLL')} · Europe/Zurich` : 'Europe/Zurich';
  }

  requestReload(): void {
    this.request$.next();
  }

  onDateChanged(value: string): void {
    this.selectedDate = value;
    this.resetPageAndReload();
  }

  onQueryChanged(value: string): void {
    this.query = value;
    this.resetPageAndReload();
  }

  onTeamChanged(value: string): void {
    this.team = value ?? '';
    this.resetPageAndReload();
  }

  onAvailabilityFilterChanged(value: CollectorAvailabilityFilter): void {
    this.availabilityFilter = value;
    this.resetPageAndReload();
  }

  onSkillChanged(value: string): void {
    this.skill = value ?? '';
    this.resetPageAndReload();
  }

  onSortChanged(value: CollectorAvailabilitySort): void {
    this.sort = value;
    this.resetPageAndReload();
  }

  onFromMinuteChanged(value: number): void {
    this.fromMinute = Math.max(0, Math.min(AVAILABILITY_DAY_MINUTES - this.sliderStep, value));
    if (this.fromMinute >= this.toMinute) {
      this.toMinute = Math.min(AVAILABILITY_DAY_MINUTES, this.fromMinute + this.sliderStep);
    }
    this.resetPageAndReload();
  }

  onToMinuteChanged(value: number): void {
    this.toMinute = Math.max(this.sliderStep, Math.min(AVAILABILITY_DAY_MINUTES, value));
    if (this.toMinute <= this.fromMinute) {
      this.fromMinute = Math.max(0, this.toMinute - this.sliderStep);
    }
    this.resetPageAndReload();
  }

  onPageChanged(event: PageEvent): void {
    this.page = event.pageIndex;
    this.pageSize = event.pageSize;
    this.requestReload();
  }

  selectKpiTeam(team: string): void {
    this.team = team;
    this.activeTab = 'collectors';
    this.resetPageAndReload();
  }

  drillIntoTeam(team: string): void {
    this.selectKpiTeam(team);
  }

  setTab(tab: AvailabilityTab): void {
    this.activeTab = tab;
  }

  toggleFullscreen(): void {
    this.fullscreen = !this.fullscreen;
    this.fullscreenChange.emit(this.fullscreen);
  }

  onPinnedChange(value: boolean): void {
    this.hasPinnedDetails = value;
  }

  requestClose(): void {
    this.close.emit();
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: KeyboardEvent): void {
    if (!this.active) {
      return;
    }
    event.preventDefault();
    if (this.hasPinnedDetails) {
      this.hasPinnedDetails = false;
      this.clearPinnedTick += 1;
      return;
    }
    if (this.fullscreen) {
      this.fullscreen = false;
      this.fullscreenChange.emit(false);
      return;
    }
    this.requestClose();
  }

  private resetPageAndReload(): void {
    this.page = 0;
    this.requestReload();
  }

  private buildSkillOptions(
    config: CollectorAvailabilitySkillConfig | null
  ): CollectorAvailabilitySkillOption[] {
    if (config?.eventSkillGroups?.length) {
      return config.eventSkillGroups
        .map((group) => ({
          label: group.label,
          codes: [...(group.codes ?? [])]
        }))
        .filter((group) => group.label && group.codes.length > 0);
    }

    return (config?.eventSkillCodes ?? []).map((code) => ({
      label: code,
      codes: [code]
    }));
  }
}
